import React from "react";
import { BadgeCheck, ExternalLink, MapPin } from "lucide-react";
import { useSeoSettings } from "../../lib/useSeoSettings";

const LINK_LABELS = {
    instagram: "Instagram", practo: "Practo", youtube: "YouTube", facebook: "Facebook",
    x: "X", linkedin: "LinkedIn", justdial: "JustDial", wikipedia: "Wikipedia",
};
const LINK_ORDER = ["instagram", "practo", "youtube", "linkedin", "facebook", "x"];

const dateLabel = (iso) => (iso
    ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
    : "");

/** Who wrote it, what they're qualified in, and who checked it — the trust signal Google wants on health pages. */
export const AuthorBox = ({ author = {}, reviewer = null, settings = {}, reviewedAt = "" }) => {
    const brand = useSeoSettings();
    if (settings.author_box_enabled === false || !author.name || author.is_brand) return null;

    const clinic = brand.clinic || {};
    const clinicLine = settings.author_box_show_clinic !== false && clinic.name
        ? [clinic.name, clinic.city].filter(Boolean).join(", ")
        : "";
    const links = settings.author_box_show_links === false ? []
        : LINK_ORDER.map((k) => ({ key: k, href: (brand.profiles || {})[k] })).filter((l) => l.href);

    return (
        <div className="mt-10 border border-white/10 bg-bg1 p-6" data-testid="blog-author-card">
            <div className="flex flex-col sm:flex-row gap-5">
                {author.photo_url ? (
                    <img src={author.photo_url} alt={author.name}
                        className="w-16 h-16 rounded-full object-cover border border-white/10 shrink-0" />
                ) : (
                    <div className="w-16 h-16 rounded-full bg-gold/15 text-gold grid place-items-center
                        font-display text-2xl shrink-0" data-testid="author-initial">{author.name[0]}</div>
                )}
                <div className="min-w-0">
                    <div className="mono text-[9px] tracking-[0.2em] text-gold">
                        {(settings.author_box_label || "Published by").toUpperCase()}
                    </div>
                    <div className="font-display text-xl uppercase tracking-tight mt-1"
                        data-testid="author-name">{author.name}</div>
                    <div className="text-sm text-zinc-400">{author.title}</div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                        {author.qualifications && (
                            <span className="mono text-[10px] tracking-[0.14em] text-lime"
                                data-testid="author-qualifications">{author.qualifications}</span>
                        )}
                        {(author.clinic || clinicLine) && (
                            <span className="inline-flex items-center gap-1.5 mono text-[10px] tracking-[0.14em] text-zinc-500"
                                data-testid="author-clinic">
                                <MapPin className="w-3 h-3" />
                                {[author.clinic || clinic.name, author.city || clinic.city].filter(Boolean).join(", ")}
                            </span>
                        )}
                        {author.registration && (
                            <span className="mono text-[10px] tracking-[0.14em] text-zinc-500">
                                Reg. {author.registration}
                            </span>
                        )}
                    </div>
                    {author.bio && <p className="text-sm text-zinc-400 mt-3 leading-relaxed">{author.bio}</p>}
                    {links.length > 0 && (
                        <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="author-links">
                            {links.map((l) => (
                                <a key={l.key} href={l.href} target="_blank" rel="noreferrer"
                                    data-testid={`author-link-${l.key}`}
                                    className="inline-flex items-center gap-1.5 mono text-[10px] tracking-[0.14em]
                                        text-zinc-400 hover:text-lime transition-colors">
                                    {LINK_LABELS[l.key] || l.key} <ExternalLink className="w-3 h-3" />
                                </a>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {reviewer?.name && (
                <div className="mt-5 pt-5 border-t border-white/10 flex flex-wrap items-center gap-x-3 gap-y-1"
                    data-testid="blog-reviewer-line">
                    <BadgeCheck className="w-4 h-4 text-lime shrink-0" />
                    <span className="text-sm text-zinc-300">
                        {settings.reviewer_label || "Medically reviewed by"}{" "}
                        <strong className="font-semibold">{reviewer.name}</strong>
                        {reviewer.qualifications ? `, ${reviewer.qualifications}` : ""}
                    </span>
                    {settings.reviewer_show_date !== false && reviewedAt && (
                        <span className="mono text-[10px] tracking-[0.14em] text-zinc-500">
                            LAST REVIEWED {dateLabel(reviewedAt).toUpperCase()}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

export default AuthorBox;
